import {SPECS} from './../specs';
import {ControllerParams} from '@wix/yoshi-flow-editor';
import {
  CheckoutNavigationService,
  DynamicPaymentMethodsShape,
  DynamicPaymentMethodsTheme,
  QueryParamsService,
  SiteStore,
  StoreMetaDataApi,
} from '@wix/wixstores-client-storefront-sdk';
import {StoresWidgetID} from '@wix/wixstores-client-core';
import {CartService} from '../services/CartService';
import {PaymentBreakdown} from '@wix/cashier-express-checkout-widget/dist/src/types/PaymentBreakdown';
import {EcommerceSettingsService} from '../services/EcommerceSettingsService';
import {CreateCheckoutExceptions, PaymentMethodType} from '../../common/constants';
import {ButtonStyle, Theme} from '@wix/cashier-express-checkout-widget/dist/src/types/Styles';
import {StyleSettingsService} from '../services/StyleSettingsService';
import {BIService} from '../services/BIService';
import {OrderService} from '../services/OrderService';
import {ICartControllerApi} from '../../types/app.types';
import {NavigationService} from '../services/NavigationService';
import {ModalManagerService} from '../services/ModalManagerService';
import {MinimumOrderAmountService} from '../services/MinimumOrderAmountService';
import {
  createExpressCheckoutButtonsController,
  ExpressCheckoutButtonsController,
} from '@wix/ecom-platform-express-checkout-buttons';
import {openNoOnlinePaymentsModal} from '../utils/openNoOnlinePaymentsModal';
import {getAdditionalFeesPrice, getCatalogAppIds, getNumberOfAdditionalFees} from '../utils/bi.utils';

export interface PaymentBreakdownAndAmount {
  paymentBreakdown: PaymentBreakdown;
  paymentAmount: string;
}

export class CashierExpressStore {
  private readonly cartService: CartService;
  private readonly navigationService: NavigationService;
  private readonly checkoutNavigationService: CheckoutNavigationService;
  private readonly ecommerceSettingsService: EcommerceSettingsService;
  private readonly biService: BIService;
  private readonly orderService: OrderService;
  private readonly modalManagerService: ModalManagerService;
  private readonly minimumOrderAmountService: MinimumOrderAmountService;
  private storeMetaData: Awaited<ReturnType<StoreMetaDataApi['getStoreMetaData']>>;
  private controllerPromise: Promise<ExpressCheckoutButtonsController>;

  constructor(
    controllerParams: ControllerParams,
    controllerApi: ICartControllerApi,
    private readonly siteStore: SiteStore,
    {
      cartService,
      checkoutNavigationService,
      ecommerceSettingsService,
      biService,
      orderService,
      navigationService,
      modalManagerService,
      minimumOrderAmountService,
    }: {
      cartService: CartService;
      checkoutNavigationService: CheckoutNavigationService;
      ecommerceSettingsService: EcommerceSettingsService;
      biService: BIService;
      orderService: OrderService;
      navigationService: NavigationService;
      modalManagerService: ModalManagerService;
      minimumOrderAmountService: MinimumOrderAmountService;
    },
    private readonly styleSettingsService: StyleSettingsService
  ) {
    this.cartService = cartService;
    this.checkoutNavigationService = checkoutNavigationService;
    this.navigationService = navigationService;
    this.ecommerceSettingsService = ecommerceSettingsService;
    this.orderService = orderService;
    this.biService = biService;
    this.modalManagerService = modalManagerService;
    this.minimumOrderAmountService = minimumOrderAmountService;

    void this.initializeExpressCheckoutButtonsController(controllerParams);
  }

  public async init() {
    /* istanbul ignore else */
    if (this.styleSettingsService.isExpressCheckoutButtonShownInSomeBreakpoint) {
      const controller = await this.controllerPromise;
      void controller.init();
    }
  }

  private readonly initializeExpressCheckoutButtonsController = (controllerParams: ControllerParams) => {
    this.controllerPromise = (async () => {
      this.storeMetaData = await this.ecommerceSettingsService.get();
      return createExpressCheckoutButtonsController({
        getCurrency: () => this.cartService.cartModel?.paymentCurrencyCode,
        demoMode: this.siteStore.isEditorMode(),
        controllerConfig: controllerParams.controllerConfig,
        httpClient: controllerParams.flowAPI.httpClient,
        onClick: () => {
          const {activePaymentMethods} = this.storeMetaData;
          this.navigationService.isNavigationToCheckoutInProcess = true;

          const isPickupFlow = this.orderService.isPickup();
          const isWithOfflinePayment =
            activePaymentMethods?.filter((paymentMethod) => paymentMethod.type === PaymentMethodType.offline).length >
            0;
          this.biService.clickOnCheckoutWithEWallet(
            this.cartService.cartModel,
            this.cartService.estimatedTotals,
            this.cartService.cartType,
            activePaymentMethods,
            isWithOfflinePayment,
            isPickupFlow
          );

          const {canCheckout, modalType} = this.checkIsAllowedToCheckout();
          if (!canCheckout) {
            const destination = this.cartService.cartModel.contactInfo?.address;
            const catalogAppId = getCatalogAppIds(this.cartService.cartModel);
            const additionalFeesPrice = getAdditionalFeesPrice(this.cartService.estimatedTotals);
            const additionalFeesNumber = getNumberOfAdditionalFees(this.cartService.estimatedTotals);
            void this.checkoutNavigationService.openLegacyCartModal(
              modalType,
              {destination, catalogAppId, additionalFeesPrice, additionalFeesNumber},
              this.styleSettingsService.isEditorX
            );
            this.navigationService.isNavigationToCheckoutInProcess = false;
          }
        },
        getCheckoutId: /* istanbul ignore next */ async () => {
          const createCheckoutResult = await this.cartService.createCheckout();
          if (
            typeof createCheckoutResult !== 'string' &&
            createCheckoutResult?.error === CreateCheckoutExceptions.siteMustAcceptPayments
          ) {
            await openNoOnlinePaymentsModal(
              this.checkoutNavigationService,
              this.styleSettingsService.isEditorX,
              this.cartService.cartModel,
              this.cartService.estimatedTotals
            );

            this.navigationService.isNavigationToCheckoutInProcess = false;
            return Promise.resolve(undefined);
          } else {
            setTimeout(() => (this.navigationService.isNavigationToCheckoutInProcess = false), 5_000);
            return Promise.resolve(this.cartService.checkoutId);
          }
        },
        widgetId: StoresWidgetID.CART,
        locale: controllerParams.flowAPI.translations.config.language,
        errorHandler: controllerParams.flowAPI.errorHandler,
      });
    })();
    return this.controllerPromise;
  };

  private readonly checkIsAllowedToCheckout = () => {
    const {hasCreatedPaymentMethods, canStoreShip, isPremium} = this.storeMetaData ?? /* istanbul ignore next */ {};
    const ecommerceSettings = this.ecommerceSettingsService.getEcommerceSettings();
    const shouldUseEcommerceSettings = this.siteStore.experiments.enabled(SPECS.UseEcommerceSettingsInCart);

    const {canCheckout, modalType, shouldNotify} = this.checkoutNavigationService.checkIsAllowedToCheckout({
      areAllItemsDigital: this.cartService.isNonShippableCart,
      hasShippableItems: this.cartService.hasShippableItems,
      isPremium: shouldUseEcommerceSettings ? ecommerceSettings.isPremium : isPremium,
      canStoreShip: shouldUseEcommerceSettings ? ecommerceSettings.delivery?.canBusinessShip : canStoreShip,
      hasCreatedPaymentMethods,
      canShipToDestination: true,
    });
    return {canCheckout, modalType, shouldNotify};
  };

  private get cashierExpressEnabled() {
    return this.cartService.areAllItemsInStock;
  }

  private readonly handlePaymentMethodError = () => {
    const queryParamsService = new QueryParamsService(this.siteStore);
    const pmName = queryParamsService.getQueryParam('pmName');
    const pmUrl = queryParamsService.getQueryParam('pmUrl') ?? '';

    if (pmName !== undefined && pmName !== '') {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.modalManagerService.modalManger.openErrorWithPaymentMethod({
        pmName: pmName.replace(/\//g, ''),
        pmUrl: pmUrl.replace(/\//g, ''),
      });
    }
  };

  private shouldRequestShipping(): boolean {
    const {shouldShowShipping} = this.styleSettingsService;
    const isPickup = shouldShowShipping && this.orderService.isPickup();
    const hasShippableItems = this.cartService.hasShippableItems;
    return hasShippableItems && !isPickup;
  }

  private getButtonStyle(): ButtonStyle {
    return {
      shape: this.styleSettingsService.dynamicPaymentMethodsShape === DynamicPaymentMethodsShape.pill ? 'pill' : 'rect',
      height: 42,
    };
  }

  private getButtonTheme(): Theme {
    return this.styleSettingsService.dynamicPaymentMethodsTheme === DynamicPaymentMethodsTheme.light ? 'light' : 'dark';
  }

  private shouldShowDynamicPaymentOptions(): boolean {
    return (
      !this.minimumOrderAmountService.shouldDisableCheckout &&
      !this.cartService?.estimatedTotals?.payLater?.total?.convertedAmount &&
      !this.cartService.hasErrorViolations
    );
  }

  private shouldShowExpressCheckout(): boolean {
    const isCardTokenizationCart = this.cartService?.cartModel?.lineItems?.some(
      (lineItem) => lineItem.savePaymentMethod
    );
    return (
      this.styleSettingsService.isExpressCheckoutButtonShownInSomeBreakpoint &&
      this.shouldShowDynamicPaymentOptions() &&
      !isCardTokenizationCart
    );
  }

  private getProps() {
    return {
      handlePaymentMethodError: this.handlePaymentMethodError,
      dynamicPaymentOptionsProps: {
        buttonStyle: this.getButtonStyle(),
        getButtonTheme: this.getButtonTheme(),
        shouldRequestShipping: this.shouldRequestShipping(),
        canCheckout: this.checkIsAllowedToCheckout()?.canCheckout,
      },
      shouldShowExpressCheckout: this.shouldShowExpressCheckout(),
    };
  }

  public toProps(): Partial<ReturnType<CashierExpressStore['getProps']>> {
    if (this.cashierExpressEnabled && this.cartService.estimatedTotals) {
      return this.getProps();
    }

    return {
      handlePaymentMethodError: this.handlePaymentMethodError,
      shouldShowExpressCheckout: false,
    };
  }
}
